import { useState, useEffect } from 'react';
import type { MetricDefinition, AnalyticsQueryInput } from '../../hooks/useAnalyticsQuery.ts';

interface QueryBuilderProps {
  metrics: MetricDefinition[];
  loading?: boolean;
  onSubmit: (input: AnalyticsQueryInput) => void;
  tenants?: { id: string; name: string }[];
  hideTenantFilter?: boolean;
}

const GRANULARITY_OPTIONS = [
  { value: 'hour', label: 'Ora' },
  { value: 'day', label: 'Giorno' },
  { value: 'week', label: 'Settimana' },
  { value: 'month', label: 'Mese' },
];

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

export function QueryBuilder({ metrics, loading, onSubmit, tenants, hideTenantFilter }: QueryBuilderProps) {
  const [metric, setMetric] = useState('');
  const [granularity, setGranularity] = useState<'hour' | 'day' | 'week' | 'month'>('day');
  const [fromDate, setFromDate] = useState(() => defaultDateRange().from);
  const [toDate, setToDate] = useState(() => defaultDateRange().to);
  const [tenantFilter, setTenantFilter] = useState('');
  const [groupByTenant, setGroupByTenant] = useState(false);
  const [compareWith, setCompareWith] = useState(false);

  // Set default metric when metrics load
  useEffect(() => {
    if (metrics.length > 0 && !metric) {
      setMetric(metrics[0].id);
    }
  }, [metrics, metric]);

  // Group metrics by domain
  const grouped: Record<string, MetricDefinition[]> = {};
  for (const m of metrics) {
    (grouped[m.domain] ??= []).push(m);
  }
  const domainLabels: Record<string, string> = {
    email: 'Email',
    web: 'Web Tracking',
    loyalty: 'Loyalty',
  };

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!metric) return;
    const input: AnalyticsQueryInput = {
      metric,
      granularity,
      timeRange: {
        from: new Date(fromDate + 'T00:00:00Z').toISOString(),
        to: new Date(toDate + 'T23:59:59.999Z').toISOString(),
      },
      ...(tenantFilter && { filters: { tenant_id: tenantFilter } }),
      ...(groupByTenant && { dimensions: ['tenant_id'] }),
      ...(compareWith && { compareWith: 'previous_period' }),
    };
    onSubmit(input);
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Metric selector */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Metrica</label>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Object.entries(grouped).map(([domain, ms]) => (
              <optgroup key={domain} label={domainLabels[domain] ?? domain}>
                {ms.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Granularity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Granularità</label>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as 'hour' | 'day' | 'week' | 'month')}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {GRANULARITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Tenant filter — hidden in tenant mode */}
        {!hideTenantFilter && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Filtro Tenant ID</label>
          <input
            type="text"
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            placeholder="Tutti i tenant"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        )}

        {/* Date range */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Periodo</label>
          <div className="flex gap-1 mb-1">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => {
                  const to = new Date();
                  const from = new Date();
                  from.setDate(from.getDate() - days);
                  setFromDate(from.toISOString().slice(0, 10));
                  setToDate(to.toISOString().slice(0, 10));
                }}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-600"
              >
                {days}g
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Compare toggle + tenant grouping */}
        <div className="flex items-end gap-3 flex-col justify-end">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={compareWith}
              onChange={(e) => setCompareWith(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            Confronta periodo
          </label>
          {tenants && tenants.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={groupByTenant}
                onChange={(e) => setGroupByTenant(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              Raggruppa per tenant
            </label>
          )}
        </div>

        <div className="flex items-end">
          <button
            type="submit"
            disabled={loading || !metric}
            className="w-full bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Query...' : 'Esegui Query'}
          </button>
        </div>
      </div>
    </form>
  );
}
